import { Button, Checkbox, CheckboxChangeEvent, Form, Input, Modal, Select } from "@findemdev/maui";
import { useState, useCallback, FC, useEffect, ChangeEvent, useMemo, useContext } from "react";
import { CredentialLibraryType, ICredential } from "../../../types/credential";
import { CapOrIndustryPracticeHeader, CapOrIndustryPracticeTitle, IncompleteFormWarning, Label, RequiredMark } from "./CreateUpdateCredential.styled";
import { QueryParams, RRA_CAPABILITY, RRA_INDUSTRY, RRA_PRACTICE } from "../../../util/constants";
import { UpdateCredentialHandler, RRA_INDUSTRY_VALUES } from "../../../types/common";
import { useIsUserCredOwner } from "../../../hooks/useIsUserCredOwner";
import ModifyLibrary from "../ModifyLibrary/ModifyLibrary";
import useShouldDisableSave from "../../../hooks/useShouldDisableSave";
import { FullStory }  from '@fullstory/browser';
import { CredBuilderLibraryEvents, FullStoryCustomEvents } from "../../../../../utils/fullstory";
import { useSearchParams } from "react-router-dom";
import { decodeString } from "@findemdev/findem-helpers";
import UserContext from "../../../../../contexts/UserContext";

interface ICreateCredentialProps {
  onClose: () => void;
  onGenerate: UpdateCredentialHandler;
  onCreateManually: (title: string) => void;
  credential?: ICredential;
}

export enum CredentialLibraryModificationType {
  ADD = "add",
  REMOVE = "remove",
}

enum SubmitDisableReason {
  INCOMPLETE_FORM = "INCOMPLETE_FORM",
  IS_GENERATING = "IS_GENERATING",
  UNKNOWN = "UNKNOWN",
  INSUFFICIENT_USER_PRIVILEGES = "INSUFFICIENT_USER_PRIVILEGES",
}

const CreateCredential: FC<ICreateCredentialProps> = ({
  onClose,
  onGenerate,
  onCreateManually: _onCreateManually,
  credential,
}) => {
  const [searchParams] = useSearchParams();
  const userModel = useContext(UserContext);

  const [title, setTitle] = useState<string>("");
  const [clientCompanyName, setClientCompanyName] = useState<string | undefined>(undefined);
  const [rraIndustry, setRraIndustry] = useState<string | undefined>(undefined);
  const [rraPractice, setRraPractice] = useState<string | undefined>(undefined);
  const [capability, setCapability] = useState<string | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSharedToLibrary, setIsSharedToLibrary] = useState<boolean>(false);
  const [isVisibleConfirmModifyLibraries, setIsVisibleConfirmModifyLibraries] = useState<boolean>(false);

  // Custom Hooks
  const isUserCredOwner = useIsUserCredOwner({ credentialParam: credential });
  const shouldDisableSave = useShouldDisableSave({ credentialParam: credential });

  const rraPracticeOptions = useMemo(() => {
    if (!rraIndustry) {
      return RRA_INDUSTRY.map((industry) => ({
        label: industry as string,
        options: RRA_PRACTICE[industry].map((practice) => ({
          key: `rra-industry-option-${industry}-${practice}`,
          label: practice,
          value: practice,
        })),

      }));
    } else {
      return [{
        label: rraIndustry,
        options: RRA_PRACTICE[rraIndustry as RRA_INDUSTRY_VALUES]?.map((industry) => ({
          key: `rra-industry-option-${rraIndustry}-${industry}`,
          label: industry,
          value: industry
        })) ?? [],
      }];
    }
  }, [rraIndustry]);

  // RRA Practice and RRA Industry are related to each other so their selected values need to be in sync with each other
  const syncRRAPractice = useCallback((selectedIndustry: RRA_INDUSTRY_VALUES) => {
    // new value of RRA Industry is selected where the current RRA Practice value doesnt belong in it, clear the RRA Practice
    if (rraPractice && !RRA_PRACTICE[selectedIndustry]?.includes(rraPractice)) {
      setRraPractice("");
    }
  }, [rraPractice]);

  const syncRRAIndustry = useCallback((selectedPractice: string) => {
    // RRA Industry is not selected but RRA Practice is selected, so populate the RRA Industry by lookup
    if (!rraIndustry && selectedPractice) {
      const derivedRRAIndustry = RRA_INDUSTRY.find((industry) => {
        return RRA_PRACTICE[industry].includes(selectedPractice);
      }) as string;

      if (derivedRRAIndustry) {
        setRraIndustry(derivedRRAIndustry);
      }
    }
  }, [rraIndustry]);

  const rraIndustryOptions = useMemo(() => RRA_INDUSTRY.map((industry) => ({
    value: industry,
    label: industry,
  })), []);

  const capabilityOptions = useMemo(() => RRA_CAPABILITY.map(value => ({
    value,
    label: value,
  })), []);

  const hasIndustry = Boolean(rraIndustry?.length);
  const hasPractice = Boolean(rraPractice?.length);
  const hasCapability = Boolean(capability?.length);
  const capOrIndustryPractice = hasIndustry ? hasPractice : hasCapability; // practice is dependent on industry, capability is independent, either one can satisfy the requirement

  const { isSubmitDisabled, submitDisableReason, submitDisableMessage } = useMemo(() => {
    const hasRequiredFields = title.length > 0 || !capOrIndustryPractice;
    const hasUserPermission = isUserCredOwner || userModel.isCredAdmin();

    const isSubmitDisabled = !hasUserPermission || !hasRequiredFields || !capOrIndustryPractice || isGenerating;
    let submitDisableReason = null;
    let submitDisableMessage = null;

    if (isSubmitDisabled) {
      if (!hasUserPermission) {
        submitDisableReason = SubmitDisableReason.INSUFFICIENT_USER_PRIVILEGES;
        submitDisableMessage = "You do not have sufficient permissions to perform this action.";
      } else if (!hasRequiredFields) {
        submitDisableReason = SubmitDisableReason.INCOMPLETE_FORM;
        submitDisableMessage = "Please complete required fields to continue.";
      } else if (isGenerating) {
        submitDisableReason = SubmitDisableReason.IS_GENERATING;
      } else {
        submitDisableReason = SubmitDisableReason.UNKNOWN;
      }
    }

    return {
      isSubmitDisabled,
      submitDisableReason,
      submitDisableMessage,
    }
  }, [title, isGenerating, capOrIndustryPractice, isUserCredOwner, userModel]);

  const { isCredLibraryModified, credLibraryModifiedType } = useMemo(() => {
    if (!credential) return { isCredLibraryModified: false, credLibraryModifiedType: null };
    const existingLibraries = credential?.libraries ?? [];
    if (isSharedToLibrary && !existingLibraries.includes(CredentialLibraryType.CRED_LIBRARY)) {
      return { isCredLibraryModified: true, credLibraryModifiedType: CredentialLibraryModificationType.ADD };
    } else if (!isSharedToLibrary && existingLibraries.includes(CredentialLibraryType.CRED_LIBRARY)) {
      return { isCredLibraryModified: true, credLibraryModifiedType: CredentialLibraryModificationType.REMOVE };
    }
    return { isCredLibraryModified: false, credLibraryModifiedType: null };
  }, [credential, isSharedToLibrary]);

  const shouldShowFormDisableHelperText = isSubmitDisabled &&
    submitDisableMessage &&
    (submitDisableReason === SubmitDisableReason.INCOMPLETE_FORM || submitDisableReason === SubmitDisableReason.INSUFFICIENT_USER_PRIVILEGES);

  const hasModifyPermission = isUserCredOwner || userModel.isCredAdmin();

  const handleCreateManually = () => {
    if (title === "") return;
    setIsGenerating(true);
    onGenerate({
      name: title,
      client_company_name: clientCompanyName,
      industry: rraIndustry ? [rraIndustry] : null,
      practice: rraPractice,
      capability,
      onEnd: () => {
        setIsGenerating(false);
      }
    });
  };

  const handleTitleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleCapability = useCallback((value: string) => {
    setCapability(value);
  }, []);

  const handleClientCompanyName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setClientCompanyName(e.target.value);
  }, []);

  const handleRraIndustry = useCallback((value: string) => {
    setRraIndustry(value);
    syncRRAPractice(value as RRA_INDUSTRY_VALUES);
  }, [syncRRAPractice]);

  const handleRraPractice = useCallback((value: string) => {
    setRraPractice(value);
    syncRRAIndustry(value);
  }, [syncRRAIndustry]);

  const onCancelModifyLibrary = () => {
    setIsVisibleConfirmModifyLibraries(false);
  }

  const handleIncludeInLibraryChange = (e: CheckboxChangeEvent) => {
    setIsSharedToLibrary(e.target.checked);
  }

  const onSubmit = async () => {
    if (credential) {
      setIsGenerating(true);

      if (isCredLibraryModified && !isVisibleConfirmModifyLibraries) {
        setIsVisibleConfirmModifyLibraries(true);
        setIsGenerating(false);
        return;
      }

      const existingLibraries = credential?.libraries ?? [];
      const effectiveLibraries = isSharedToLibrary ?
        [...existingLibraries, CredentialLibraryType.CRED_LIBRARY] :
        existingLibraries.filter(lib => lib !== CredentialLibraryType.CRED_LIBRARY);
      const updatedLibraries = Array.from(new Set(effectiveLibraries));

      await onGenerate({
        name: title,
        client_company_name: clientCompanyName,
        industry: rraIndustry ? [rraIndustry] : null,
        practice: rraPractice,
        capability,
        libraries: updatedLibraries,
        onEnd: () => {
          setIsGenerating(false);
          setIsVisibleConfirmModifyLibraries(false);
        }
      });

      // track changes to cred library
      if (isCredLibraryModified) {
        const eventName = credLibraryModifiedType === CredentialLibraryModificationType.ADD ? CredBuilderLibraryEvents.AddedToLibrary : CredBuilderLibraryEvents.RemovedFromLibrary;
        FullStory('trackEvent', {
          name: FullStoryCustomEvents.CredentialBuilder,
          properties: {
            event: eventName,
            credentialId: credential.ref,
            homeTab: searchParams.get(QueryParams.HomeTab),
          }
        });
      }
    } else {
      handleCreateManually();
    }
  };

  useEffect(() => {
    if (credential) {
      setTitle(decodeString(credential.name));
      setIsSharedToLibrary(credential.libraries?.includes(CredentialLibraryType.CRED_LIBRARY) ?? false);
      if (credential.client_company_name) {
        setClientCompanyName(decodeString(credential.client_company_name));
      }
      if (credential.industry) {
        setRraIndustry(credential.industry[0]);
      }
      if (credential.practice) {
        setRraPractice(credential.practice);
      }
      if (credential.capability) {
        setCapability(credential.capability);
      }
    }
  }, [credential]);

  return (
    <>
      <Modal
        title={credential ? "Update Credential" : "New Credential"}
        open={true}
        onCancel={onClose}
        destroyOnClose={true}
        footer={[
          <Button
            key="cancel"
            type="default"
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          ,
          <Button
            key="submit"
            type="primary"
            onClick={onSubmit}
            loading={isGenerating}
            disabled={isSubmitDisabled || !hasModifyPermission}
          >
            {credential ? "Update Credential" : "Create Credential"}
          </Button>
        ]}
      >
        <Form layout="vertical" initialValues={{ size: "default" }}>
          <Form.Item label="Credential Name" required>
            <Input
              width={"100%"}
              defaultValue={title}
              onChange={handleTitleChange}
              placeholder="Enter your Credential name"
              disabled={shouldDisableSave}
            />
          </Form.Item>
          <Form.Item label={<Label>Company Name (Client)</Label>}>
            <Input
              width={"100%"}
              defaultValue={clientCompanyName}
              onChange={handleClientCompanyName}
              placeholder="Enter potential client name"
              disabled={shouldDisableSave}
            />
          </Form.Item>
          <CapOrIndustryPracticeHeader>
            <CapOrIndustryPracticeTitle variant="boldData">
              <RequiredMark>* </RequiredMark>Please select RRA Industry and/or Capability
            </CapOrIndustryPracticeTitle>
          </CapOrIndustryPracticeHeader>
          <Form.Item label={<Label>RRA Industry</Label>}>
            <Select
              options={rraIndustryOptions}
              onChange={handleRraIndustry}
              getPopupContainer={trigger => trigger.parentNode}
              value={rraIndustry}
              placeholder="Select an industry"
              disabled={shouldDisableSave}
            />
          </Form.Item>
          <Form.Item label={<Label>RRA Practice</Label>} required={hasIndustry}>
            <Select
              showSearch
              options={rraPracticeOptions}
              onChange={handleRraPractice}
              getPopupContainer={trigger => trigger.parentNode}
              placeholder="Select an practice area"
              value={rraPractice}
              disabled={shouldDisableSave}
            />
          </Form.Item>
          <Form.Item
            label={<Label>Capability</Label>}
          >
            <Select
              showSearch
              placeholder='Select capability'
              value={capability}
              options={capabilityOptions}
              onChange={handleCapability}
              disabled={shouldDisableSave}
            />
          </Form.Item>
          <>
            <Checkbox disabled={!hasModifyPermission} checked={isSharedToLibrary} onChange={handleIncludeInLibraryChange}>
              Include in Cred Library
            </Checkbox>
            {shouldShowFormDisableHelperText &&
              <IncompleteFormWarning type="warning" message={submitDisableMessage} />
            }
          </>
        </Form>
      </Modal>
      {isVisibleConfirmModifyLibraries && credLibraryModifiedType &&
        <ModifyLibrary
          isVisibleConfirmModifyLibraries={isVisibleConfirmModifyLibraries}
          credLibraryModifiedType={credLibraryModifiedType}
          isSubmitDisabled={isSubmitDisabled}
          onCancel={onCancelModifyLibrary}
          onSubmit={onSubmit}
        />
      }
    </>
  );
};

export default CreateCredential;
